import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { SEGMENT_PRIMARY_FILTER_MORE, SEGMENT_PRIMARY_FILTER_TOP } from "../constants";
import {
  boardFiltersAreActive,
  clearFloorFilters,
  floorFiltersAreActive,
  loadAttributionLabelOptions,
  type CampaignFilters,
  type MyRatingFilter,
} from "../campaignFilters";
import { WalkIcon } from "../components/BoothNavButton";
import { FilterPill, segmentChipLabel } from "../components/FilterPill";

const AI_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "Any" },
  { value: "3", label: "3+" },
  { value: "4", label: "4+" },
  { value: "5", label: "5" },
];

const RATING_OPTIONS: Array<{ value: MyRatingFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "high", label: "High" },
  { value: "med", label: "Med" },
  { value: "unrated", label: "Unrated" },
];

function AttributionSelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const label = value.trim() ? value : "All";

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const maxHeight = 240;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const top = spaceBelow < 132 ? Math.max(8, rect.top - maxHeight - 4) : rect.bottom + 4;
    setMenuStyle({ top, left: rect.left, width: rect.width, maxHeight });
  }, [open, options.length, value]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const node = event.target as Node;
      if (buttonRef.current?.contains(node) || menuRef.current?.contains(node)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`floor-filter-select${open ? " is-open" : ""}`}
        aria-label="Attribution"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="floor-filter-select-label">{label}</span>
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="floor-filter-select-menu"
              role="listbox"
              aria-label="Attribution"
              style={menuStyle}
            >
              <button
                type="button"
                role="option"
                aria-selected={!value.trim()}
                className="floor-filter-select-option"
                onClick={() => {
                  onChange("");
                  setOpen(false);
                }}
              >
                All
              </button>
              {options.map((choice) => (
                <button
                  key={choice}
                  type="button"
                  role="option"
                  aria-selected={choice === value}
                  className="floor-filter-select-option"
                  onClick={() => {
                    onChange(choice);
                    setOpen(false);
                  }}
                >
                  {choice}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function FloorFilterPanel({
  value,
  onChange,
  includeOwner = false,
  attributionOptions = [],
}: {
  value: CampaignFilters;
  onChange: (next: CampaignFilters) => void;
  includeOwner?: boolean;
  attributionOptions?: string[];
}) {
  return (
    <div className="floor-filter-panel">
      <section className="floor-filter-section">
        <h3>Include</h3>
        <FilterPill
          className="floor-filter-toggle"
          label="My favorites"
          pressed={value.favoritesOnly}
          onClick={() => onChange({ ...value, favoritesOnly: !value.favoritesOnly })}
        />
        {includeOwner ? (
          <FilterPill
            className="floor-filter-toggle"
            label={
              <>
                <WalkIcon size={16} />
                Walk
              </>
            }
            ariaLabel="Walk"
            pressed={value.ownerMe}
            onClick={() => onChange({ ...value, ownerMe: !value.ownerMe })}
          />
        ) : null}
      </section>
      <section className="floor-filter-section">
        <h3>AI score</h3>
        <div className="floor-filter-exclusive" role="group" aria-label="AI score">
          {AI_OPTIONS.map((option) => (
            <FilterPill
              key={option.label}
              className="floor-filter-choice"
              label={option.label}
              pressed={value.minAi === option.value}
              onClick={() => onChange({ ...value, minAi: option.value })}
            />
          ))}
        </div>
      </section>
      <section className="floor-filter-section">
        <h3>My rating</h3>
        <div className="floor-filter-exclusive" role="group" aria-label="My rating">
          {RATING_OPTIONS.map((option) => (
            <FilterPill
              key={option.value}
              className="floor-filter-choice"
              label={option.label}
              pressed={value.myRating === option.value}
              onClick={() => onChange({ ...value, myRating: option.value })}
            />
          ))}
        </div>
      </section>
      <section className="floor-filter-section" aria-label="Segment">
        <h3>Segment</h3>
        <FilterPill
          className="floor-filter-toggle"
          label="All"
          pressed={!value.segment}
          onClick={() => onChange({ ...value, segment: "" })}
        />
        <p className="floor-filter-subhead">Priority</p>
        <div className="floor-filter-chips">
          {SEGMENT_PRIMARY_FILTER_TOP.map((label) => (
            <FilterPill
              key={label}
              className="floor-filter-chip"
              label={segmentChipLabel(label)}
              pressed={value.segment === label}
              onClick={() => onChange({ ...value, segment: label })}
            />
          ))}
        </div>
        <p className="floor-filter-subhead">More</p>
        <div className="floor-filter-chips">
          {SEGMENT_PRIMARY_FILTER_MORE.map((label) => (
            <FilterPill
              key={label}
              className="floor-filter-chip"
              label={segmentChipLabel(label)}
              pressed={value.segment === label}
              onClick={() => onChange({ ...value, segment: label })}
            />
          ))}
        </div>
      </section>
      {includeOwner ? (
        <section className="floor-filter-section">
          <h3>Attribution</h3>
          <AttributionSelect
            value={value.attribution}
            options={attributionOptions}
            onChange={(attribution) => onChange({ ...value, attribution })}
          />
        </section>
      ) : null}
    </div>
  );
}

export function FloorFilterDrawer({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  matchCount,
  highlightPending = false,
  includeOwner = false,
  eventKey,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: CampaignFilters;
  onFiltersChange: (next: CampaignFilters) => void;
  matchCount?: number;
  highlightPending?: boolean;
  includeOwner?: boolean;
  eventKey?: string;
}) {
  const [loadedAttribution, setLoadedAttribution] = useState<string[]>([]);

  useEffect(() => {
    if (!includeOwner || !open || !eventKey) return;
    let cancelled = false;
    void loadAttributionLabelOptions(eventKey).then((labels) => {
      if (!cancelled) setLoadedAttribution(labels);
    });
    return () => {
      cancelled = true;
    };
  }, [includeOwner, open, eventKey]);

  const attributionOptions = useMemo(() => {
    const selected = filters.attribution.trim();
    if (!selected || loadedAttribution.some((label) => label.toLowerCase() === selected.toLowerCase())) {
      return loadedAttribution;
    }
    return [selected, ...loadedAttribution];
  }, [filters.attribution, loadedAttribution]);

  const canClear = includeOwner ? boardFiltersAreActive(filters) : floorFiltersAreActive(filters);
  const summary =
    matchCount == null
      ? null
      : highlightPending
        ? "Resolving accounts…"
        : matchCount === 1
          ? "1 booth on this hall"
          : `${matchCount} booths on this hall`;

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  return (
    <>
      {open ? (
        <button
          type="button"
          className="floor-drawer-scrim"
          aria-label="Close filters"
          onClick={() => onOpenChange(false)}
        />
      ) : null}
      <aside className={`floor-drawer floor-filter-drawer${open ? " open" : ""}`}>
        {open ? (
          <div id="floor-filter-drawer-panel" className="floor-drawer-panel">
            <button
              type="button"
              className="floor-drawer-close"
              aria-label="Close filters"
              onClick={() => onOpenChange(false)}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                <path
                  d="M2 2l10 10M12 2L2 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <div className="floor-drawer-header">
              <span className="floor-drawer-peek-copy">
                Filters
                {summary ? <span className="muted"> · {summary}</span> : null}
              </span>
            </div>
            <div className="floor-drawer-body floor-filter-body">
              <div className="floor-filter-scroll">
                <FloorFilterPanel
                  value={filters}
                  onChange={onFiltersChange}
                  includeOwner={includeOwner}
                  attributionOptions={attributionOptions}
                />
              </div>
              <div className="floor-filter-footer">
                <button
                  type="button"
                  className="ghost filter-clear floor-filter-clear"
                  disabled={!canClear}
                  onClick={() => onFiltersChange(clearFloorFilters(filters))}
                >
                  Clear all
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </aside>
    </>
  );
}
